import { useEffect } from "react";
import { usePostHog } from "posthog-js/react";
import { GoogleLoginButton } from "@/features/auth/components/GoogleLoginButton";

export function LoginPage() {
  const posthog = usePostHog();

  useEffect(() => {
    posthog.capture("auth:page_view");
  }, [posthog]);

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-background">
      {/* Background layers with animation */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Animated gradient orbs */}
        <div
          className="absolute w-[600px] h-[600px] rounded-full blur-3xl opacity-20"
          style={{
            background: "oklch(0.75 0.15 55)",
            top: "20%",
            left: "30%",
            animation: "drift 20s ease-in-out infinite",
          }}
        />
        <div
          className="absolute w-[500px] h-[500px] rounded-full blur-3xl opacity-10"
          style={{
            background: "oklch(0.65 0.12 55)",
            bottom: "10%",
            right: "20%",
            animation: "drift 25s ease-in-out infinite reverse",
          }}
        />

        {/* Radial gradient - creates depth and focus */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(
                ellipse 80% 60% at 50% 40%,
                oklch(0.24 0.02 55 / 0.4) 0%,
                transparent 70%
              )
            `,
          }}
        />

        {/* Subtle grid pattern with mask */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
              linear-gradient(to right, var(--foreground) 1px, transparent 1px),
              linear-gradient(to bottom, var(--foreground) 1px, transparent 1px)
            `,
            backgroundSize: "80px 80px",
            maskImage: "radial-gradient(ellipse 60% 50% at 50% 50%, black 0%, transparent 70%)",
            WebkitMaskImage: "radial-gradient(ellipse 60% 50% at 50% 50%, black 0%, transparent 70%)",
          }}
        />

        {/* Ambient glow at bottom */}
        <div
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px]"
          style={{
            background: `
              radial-gradient(
                ellipse at center bottom,
                oklch(0.75 0.15 55 / 0.12) 0%,
                transparent 70%
              )
            `,
          }}
        />
      </div>

      {/* Keyframes for drift animation */}
      <style>{`
        @keyframes drift {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(30px, -20px) scale(1.05); }
          50% { transform: translate(-20px, 30px) scale(0.95); }
          75% { transform: translate(-30px, -10px) scale(1.02); }
        }
      `}</style>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-6">
        {/* Brand section */}
        <div
          className="text-center mb-10 sm:mb-16 animate-in fade-in slide-in-from-bottom-4 duration-700"
          style={{ animationDelay: "100ms", animationFillMode: "backwards" }}
        >
          {/* Brand name - editorial serif */}
          <h1
            className="text-5xl sm:text-6xl font-serif font-medium tracking-tight text-foreground mb-3"
            style={{ fontFeatureSettings: '"ss01" 1' }}
          >
            Koach
          </h1>

          {/* Tagline */}
          <p className="text-foreground-muted text-base sm:text-lg font-light tracking-wide">
            Your AI running buddy who always knows
          </p>
        </div>

        {/* Login card */}
        <div
          className="w-full max-w-sm animate-in fade-in slide-in-from-bottom-4 duration-700"
          style={{ animationDelay: "300ms", animationFillMode: "backwards" }}
        >
          <div className="relative group">
            {/* Card glow on hover */}
            <div
              className="absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100
                         transition-opacity duration-500 blur-sm"
              style={{
                background: `linear-gradient(
                  135deg,
                  oklch(0.75 0.15 55 / 0.3) 0%,
                  transparent 50%,
                  oklch(0.75 0.15 55 / 0.1) 100%
                )`,
              }}
            />

            {/* Card */}
            <div
              className="relative bg-card/80 backdrop-blur-sm border border-border
                         rounded-2xl p-8 shadow-xl"
            >
              {/* Top accent line */}
              <div
                className="absolute top-0 left-8 right-8 h-px"
                style={{
                  background: `linear-gradient(
                    90deg,
                    transparent 0%,
                    var(--primary) 50%,
                    transparent 100%
                  )`,
                  opacity: 0.5,
                }}
              />

              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-xl font-medium text-foreground mb-1.5">
                    Hey, runner!
                  </h2>
                  <p className="text-sm sm:text-base text-foreground-muted">
                    Let's get you moving
                  </p>
                </div>

                <GoogleLoginButton />

                <p className="text-xs text-foreground-subtle text-center leading-relaxed">
                  By continuing, you agree to our{" "}
                  <a
                    href="#"
                    className="text-foreground-muted hover:text-primary transition-colors underline underline-offset-2"
                  >
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://koach.run/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-foreground-muted hover:text-primary transition-colors underline underline-offset-2"
                  >
                    Privacy Policy
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer
          className="mt-10 sm:mt-16 animate-in fade-in duration-700"
          style={{ animationDelay: "500ms", animationFillMode: "backwards" }}
        >
          {/* Decorative running track line */}
          <div className="max-w-xs mx-auto mb-4 relative">
            <div
              className="h-px w-full"
              style={{
                background: `linear-gradient(
                  90deg,
                  transparent 0%,
                  oklch(0.75 0.15 55 / 0.3) 20%,
                  oklch(0.75 0.15 55 / 0.5) 50%,
                  oklch(0.75 0.15 55 / 0.3) 80%,
                  transparent 100%
                )`,
              }}
            />
            {/* Animated runner dot */}
            <div
              className="absolute top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full"
              style={{
                background: "oklch(0.75 0.15 55)",
                boxShadow: "0 0 8px oklch(0.75 0.15 55 / 0.6)",
                animation: "runnerDot 4s ease-in-out infinite",
              }}
            />
          </div>

          {/* Main footer content */}
          <div className="flex items-center justify-center gap-2 group">
            <span className="text-xs text-foreground-muted tracking-wide">
              Made with
            </span>

            {/* Animated heartbeat */}
            <span
              className="inline-flex items-center justify-center relative"
              style={{ animation: "heartbeat 1.2s ease-in-out infinite" }}
            >
              <span className="text-sm">❤️</span>
              {/* Pulse ring */}
              <span
                className="absolute inset-0 rounded-full"
                style={{
                  animation: "heartPulse 1.2s ease-out infinite",
                  background: "oklch(0.65 0.2 25 / 0.3)",
                }}
              />
            </span>

            <span className="text-xs text-foreground-muted tracking-wide">
              for
            </span>

            {/* "runners" with running figure animation on hover */}
            <span className="relative inline-flex items-center gap-1.5 text-xs group/runners">
              <span
                className="font-medium bg-clip-text text-transparent"
                style={{
                  backgroundImage: `linear-gradient(
                    135deg,
                    oklch(0.75 0.15 55) 0%,
                    oklch(0.7 0.18 40) 100%
                  )`,
                }}
              >
                runners
              </span>
              {/* Mini running figure that animates on hover */}
              <svg
                viewBox="0 0 16 16"
                className="w-3 h-3 opacity-0 group-hover/runners:opacity-100 transition-all duration-300
                           group-hover/runners:translate-x-0.5"
                style={{ color: "oklch(0.75 0.15 55)" }}
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="10" cy="3" r="1.5" fill="currentColor" />
                <path d="M8 6c-1 1.5-2 2.5-3.5 3" />
                <path d="M7 7.5L5 12l-2 1" />
                <path d="M5 11l2.5-1.5 2 3" />
                <path d="M7.5 9.5l3-1 2 .5" />
              </svg>
            </span>
          </div>

          {/* Keyframes for footer animations */}
          <style>{`
            @keyframes heartbeat {
              0%, 100% { transform: scale(1); }
              15% { transform: scale(1.15); }
              30% { transform: scale(1); }
              45% { transform: scale(1.1); }
              60% { transform: scale(1); }
            }
            @keyframes heartPulse {
              0% { transform: scale(0.8); opacity: 0.6; }
              50% { transform: scale(1.8); opacity: 0; }
              100% { transform: scale(0.8); opacity: 0; }
            }
            @keyframes runnerDot {
              0%, 100% { left: 10%; opacity: 0; }
              10% { opacity: 1; }
              90% { opacity: 1; }
              100% { left: 90%; opacity: 0; }
            }
          `}</style>
        </footer>
      </div>
    </div>
  );
}
